<template>
    <div>
        <radio-buttons
            list-heading="App.RadioButtonListHeader.SelectTopic"
            :radio-button-list="radioButtonList"
            :default-radio-button="selectedRadioButtonData.labelText"
            :sub-topic="subTopic"
            :select-by-url="true"
            @selected-radio-button="handleSelectedRadioButton"
            @radio-buttons-changed="handleRadioButtonsChanged"
        />
        <checkbox-list
            v-show="showOfficeTypes"
            list-heading="App.Vesiliikenne.CheckboxList.PalveluPisteenTyyppi"
            :checkbox-list="officeTypesCheckboxList"
            @checkbox-item="handleOfficeTypesCheckboxValue"
        />
        <div
            v-show="showSearchAddress"
            class="search-components"
        >
            <search-address />
            <locate-me class="locate-me-wrapper" />
        </div>
        <area-lists
            v-show="showAreaSelection"
            list-heading="App.RadioButtonListHeader.ValitseAlue"
            @selected-radio-button="handleAreaSelectionRadioButton"
            @selected-list-value="handleAreaSelectionListValue"
        />
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch, Prop } from "vue-facing-decorator"
    import ApiService from "@/services/ApiService"
    import SearchAddress from "@/components/SearchAddress.vue"
    import LocateMeLink from "@/components/LocateMeLink.vue"
    import RadioButtonListWithDropdown from "@/components/RadioButtonListWithDropdown.vue"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import CheckboxList from "@/components/CheckboxList.vue"
    import CheckboxModel from "@/models/checkboxmodel"
    import SearchResultModel from "@/models/searchresultmodel"
    import MunicipalitiesAndProvinces from "@/components/MunicipalitiesAndProvinces.vue"
    import { store } from '@/store/store'

    @Component({
        components: {
            "radio-buttons": RadioButtonListWithDropdown,
            "checkbox-list": CheckboxList,
            "locate-me": LocateMeLink,
            "search-address": SearchAddress,
            "area-lists": MunicipalitiesAndProvinces
        }
    })
    export default class Vesiliikenne extends Vue {
        @Prop({ type: String }) subTopic!: string

        showAreaSelection = false
        showSearchAddress = false
        showOfficeTypes: boolean = true

        apiService: ApiService = new ApiService(store.getters.baseHref)

        pageName: string = "vesiliikenne"

        // DataPublishServices rajapintakutsun etuosa
        apiCallBasePart: string = "v1/VesiLiikenne/"

        // Palvelupiste checkboxin alustus
        officeTypesCheckboxList: Array<CheckboxModel>
            = [new CheckboxModel("App.VesiLiikenne.yleisetpisteet", "YleisetPisteet", true),
               new CheckboxModel("App.VesiLiikenne.omatrekisteroinnit", "OmatRekisteroinnit", true)]

        radioButtonList: Array<RadioButtonModel> = this.radioButtonListValues

        // Valittu radiobutton tietoineen
        selectedRadioButtonData: RadioButtonModel = this.defaultRadioButton

        selectedOfficeTypesFilters: string[] = ["YleisetPisteet", "OmatRekisteroinnit"]

        async activated() {
            store.dispatch("setCurrentPage", this.pageName)
            this.radioButtonList = this.radioButtonListValues
            this.selectedRadioButtonData = this.defaultRadioButton
            store.dispatch("setSelectedSubjectRadioButton", this.selectedRadioButtonData)
            // Tehdään apikutsu, jolla haetaan kartan data
            await this.setDataToLoad(this.selectedRadioButtonData)
        }

        deactivated() {
            this.radioButtonList = []
            store.dispatch("setSubTopic", null)
            store.dispatch("setCurrentPage", "")
            store.dispatch("setSelectedAreaRadioButton", "")
            store.dispatch("setSelectedMunicipality", "")
            store.dispatch("setSelectedProvince", "")
        }

        async handleOfficeTypesCheckboxValue(item: CheckboxModel) {
            // palvelupiste tyyppien filttereiden asettaminen
            this.setOfficeTypesFilters(item)
            this.setDataToLoad(this.selectedRadioButtonData)
        }

        get currentPage(): string {
            return store.getters.currentPage
        }

        get activeLang(): SearchResultModel {
            return store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // Kielivalinta muuttunut. Ladataan data uudestaan.
            if (this.currentPage !== this.pageName) {
                return
            }
            this.updateData(store.getters.rightSlot)
        }

        get defaultRadioButton(): RadioButtonModel {
            // Jos sivulle tullaan subTopic routen kautta, niin asetetaan default radiobutton value routen mukaisesti
            if (this.subTopic) {
                for (let i = 0; i < this.radioButtonList.length; i++) {
                    if (this.radioButtonList[i].radioValue === this.subTopic) {
                        return this.radioButtonList[i]
                    }
                }
            }
            return this.radioButtonList[0]
        }

        async setOfficeTypesFilters(item: CheckboxModel) {
            const index = this.selectedOfficeTypesFilters.indexOf(item.checkboxValue)
            index === -1 ? this.selectedOfficeTypesFilters.push(item.checkboxValue) : this.selectedOfficeTypesFilters.splice(index, 1)
        }

        async setDataToLoad(selectedRadio: RadioButtonModel): Promise<void> {
            if (selectedRadio !== this.selectedRadioButtonData) {
                return
            }

            if (this.currentPage !== this.pageName) {
                return
            }
            this.updateData(selectedRadio.radioValue)
        }

        async updateData(radioValue: string): Promise<void> {
            store.dispatch("setLoading", { name: "vesiliikenne", value: true })

            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", radioValue)

            this.updateComponentVisibility(radioValue)
            const clearLocationMarker = store.getters.currentPage.toLowerCase() !== store.getters.dataUrlToLoad?.url?.split("/")[1]?.toLowerCase()

            if (radioValue === "Rekisterointitoimipaikat") {
                let filter: string = ""
                // rajapinnassa tyyppi muuttuja joka on string,
                // jos tyyppi tyhjä niin haetaan sekä YleisetPisteet että OmatRekisteroinnit.
                // Tyyppi rajauksena voi olla  'YleisetPisteet' tai 'OmatRekisteroinnit' (non case-sensitive)
                if (this.selectedOfficeTypesFilters.length === 0) { // jos kumpikaan tyyppi ei ole valittuna niin lähetetään tyhjä arvo
                    filter = ""
                }
                else if (this.selectedOfficeTypesFilters.length === this.officeTypesCheckboxList.length) { // jos kaikki tyypit valittuna
                    filter = "kaikki"
                }
                else {
                    filter = this.selectedOfficeTypesFilters[0].toString() // otetaan valittu tyyppi rajausehdoksi
                }

                store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + radioValue + "?Tyyppi=" + filter, type: radioValue, clearLocationMarker })
            }
            else if (radioValue === "RekisteroidytVesikulkuneuvot") {
                const tieliikenteenTilastotApiUrl = "v1/TieLiikenne/TieliikenteenTilastot/"
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKuntaKohtainen?type=" + radioValue, type: radioValue, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotMaakuntaKohtainen?type=" + radioValue, type: radioValue, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: tieliikenteenTilastotApiUrl + "LiikenteenTilastotKokoMaa?type=" + radioValue + "", type: radioValue, clearLocationMarker })
                }
            }
            store.dispatch("setLoading", { name: "vesiliikenne", value: false })
        }

        get leftSlotHeaderTieliikenne(): string | undefined {
            return this.$t("App.LeftSlot.Header.VesiLiikenne")
        }

        get radioButtonListValues(): Array<RadioButtonModel> {
            return [new RadioButtonModel("App.VesiLiikenne.SubjectRadioList.LupaJaRekisterointiPaikat", "Rekisterointitoimipaikat"),
                    new RadioButtonModel("App.VesiLiikenne.SubjectRadioList.RekisteroidytVesikulkuneuvot", "RekisteroidytVesikulkuneuvot")
            ]
        }

        // Aihevalinta muuttunut
        async handleSelectedRadioButton(item: RadioButtonModel) {
            this.selectedRadioButtonData = item
            store.dispatch("setSelectedSubjectRadioButton", item)
            // Tehdään apikutsu, jolla haetaan kartan data
            await this.setDataToLoad(this.selectedRadioButtonData)
        }

        async handleRadioButtonsChanged(list: Array<RadioButtonModel>) {
            this.updateComponentVisibility(list[0].radioValue)
            // Päivitä defaultina valittu radio button
            this.selectedRadioButtonData = list[0]
        }

        updateComponentVisibility(radioValue: string) {
            this.showAreaSelection = false
            this.showSearchAddress = false
            this.showOfficeTypes = false

            if (radioValue === "Rekisterointitoimipaikat") {
                this.showOfficeTypes = true
                this.showSearchAddress = true
            }
            else if (radioValue === "RekisteroidytVesikulkuneuvot") {
                this.showAreaSelection = true
            }
        }

        handleAreaSelectionRadioButton(item: RadioButtonModel): void {
            // Kunta/Maakunta/Koko Suomi valinta vaihtunut
            store.dispatch("setSelectedAreaRadioButton", String(item))
            this.updateData(store.getters.rightSlot)
        }

        handleAreaSelectionListValue(item: string): void {
            // Valittu kunta tai maakunta dropdownista
            if (store.getters.selectedAreaRadioButton === "kunta") {
                store.dispatch("setSelectedMunicipality", item)
            }
            else if (store.getters.selectedAreaRadioButton === "maakunta") {
                store.dispatch("setSelectedProvince", item)
            }
        }
    }
</script>
<style scoped>

    .dropdowns {
        margin-bottom: 20px;
    }
</style>
